import { Dungeon } from './Dungeon';
import { CellType, DungeonConfig } from '../types';
import { randomInt, shuffle } from '../utils/random';

interface Direction {
  dx: number;
  dz: number;
}

const DIRECTIONS: Direction[] = [
  { dx: 0, dz: -2 },  // North
  { dx: 2, dz: 0 },   // East
  { dx: 0, dz: 2 },   // South
  { dx: -2, dz: 0 }   // West
];

export function generateDungeon(config: DungeonConfig): Dungeon {
  const dungeon = new Dungeon(config);

  // Start from a random odd position (ensures walls between paths)
  const startX = 1 + randomInt(0, (config.width - 2) / 2) * 2;
  const startZ = 1 + randomInt(0, (config.height - 2) / 2) * 2;

  // Carve the maze using recursive backtracking
  carve(dungeon, startX, startZ);

  // Add some rooms for variety
  addRooms(dungeon, 3);

  // Add loops by removing internal walls to create multiple routes
  addLoops(dungeon, 0.15);

  return dungeon;
}

function carve(dungeon: Dungeon, x: number, z: number): void {
  dungeon.setCell(x, z, CellType.OPEN);

  const shuffledDirs = shuffle([...DIRECTIONS]);

  for (const dir of shuffledDirs) {
    const newX = x + dir.dx;
    const newZ = z + dir.dz;

    // Check if the new position is within bounds and is still solid
    if (newX > 0 && newX < dungeon.width - 1 &&
        newZ > 0 && newZ < dungeon.height - 1 &&
        !dungeon.isWalkable(newX, newZ)) {
      // Carve the wall between current cell and new cell
      dungeon.setCell(x + dir.dx / 2, z + dir.dz / 2, CellType.OPEN);
      carve(dungeon, newX, newZ);
    }
  }
}

function addRooms(dungeon: Dungeon, count: number): void {
  for (let i = 0; i < count; i++) {
    // Random room size (3x3 to 5x5)
    const roomWidth = 3 + randomInt(0, 3);
    const roomHeight = 3 + randomInt(0, 3);

    // Random position (avoiding edges)
    const roomX = 2 + randomInt(0, dungeon.width - roomWidth - 4);
    const roomZ = 2 + randomInt(0, dungeon.height - roomHeight - 4);

    // Carve the room
    for (let z = roomZ; z < roomZ + roomHeight; z++) {
      for (let x = roomX; x < roomX + roomWidth; x++) {
        dungeon.setCell(x, z, CellType.OPEN);
      }
    }
  }
}

// Find walls that can be removed to create loops (walls with open cells on opposite sides)
function findBreakableWalls(dungeon: Dungeon): { x: number; z: number }[] {
  const breakable: { x: number; z: number }[] = [];

  for (let z = 1; z < dungeon.height - 1; z++) {
    for (let x = 1; x < dungeon.width - 1; x++) {
      if (dungeon.isWalkable(x, z)) continue;

      // Check if wall separates two open cells horizontally
      if (dungeon.isWalkable(x - 1, z) && dungeon.isWalkable(x + 1, z)) {
        breakable.push({ x, z });
        continue;
      }

      // Check if wall separates two open cells vertically
      if (dungeon.isWalkable(x, z - 1) && dungeon.isWalkable(x, z + 1)) {
        breakable.push({ x, z });
      }
    }
  }

  return breakable;
}

// Add loops to the maze by removing a fraction of internal walls
function addLoops(dungeon: Dungeon, fraction: number): void {
  const breakable = findBreakableWalls(dungeon);
  shuffle(breakable);

  const removeCount = Math.floor(breakable.length * fraction);
  for (let i = 0; i < removeCount; i++) {
    const wall = breakable[i];
    dungeon.setCell(wall.x, wall.z, CellType.OPEN);
  }
}

export function selectCeilingLightPositions(dungeon: Dungeon, spacing = 5): { x: number; z: number }[] {
  const positions: { x: number; z: number }[] = [];
  for (let z = spacing; z < dungeon.height; z += spacing) {
    for (let x = spacing; x < dungeon.width; x += spacing) {
      if (dungeon.isWalkable(x, z)) {
        positions.push({ x, z });
      }
    }
  }
  return positions;
}

export function createDebugDungeon(config: DungeonConfig): Dungeon {
  const dungeon = new Dungeon(config);

  // Create a single large room with solid borders
  // All border cells stay solid (default), interior becomes open
  for (let z = 1; z < config.height - 1; z++) {
    for (let x = 1; x < config.width - 1; x++) {
      dungeon.setCell(x, z, CellType.OPEN);
    }
  }
  // Add some internal solid obstacles for testing
  dungeon.setCell(13, 4, CellType.SOLID);
  dungeon.setCell(13, 6, CellType.SOLID);
  // dungeon.setCell(13, 8, CellType.SOLID);
  dungeon.setCell(14, 5, CellType.SOLID);
  dungeon.setCell(14, 7, CellType.SOLID);

  return dungeon;
}
